import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

gsap.registerPlugin(ScrollTrigger);

const initializedMedia = new WeakSet();
const DESKTOP_CURSOR_QUERY = '(min-width: 1034px) and (hover: hover) and (pointer: fine)';
const MAX_RENDER_FPS = 60;
const FRAME_INTERVAL = 1000 / MAX_RENDER_FPS;
const FRAME_TOLERANCE = 0.1;
const ACTIVE_PIXEL_RATIO_LIMIT = 1.5;
const FULL_PIXEL_RATIO_LIMIT = 2;
const QUALITY_RESTORE_DELAY = 120;
const SCREEN_WIDTH = 29.4;
const SCREEN_HEIGHT = 20;
const laptopPose = {
  position: new THREE.Vector3(-13, -11, -10),
  rotation: new THREE.Euler(0, 0.1 * Math.PI, 0),
  scale: 1.9,
  lidRotation: -0.1 * Math.PI,
};

const loadGltf = (loader, url) => new Promise((resolve, reject) => {
  loader.load(url, resolve, undefined, reject);
});

const loadTexture = (loader, url) => new Promise((resolve, reject) => {
  loader.load(url, resolve, undefined, reject);
});

const disposeMaterial = (material) => {
  if (Array.isArray(material)) {
    material.forEach(disposeMaterial);

    return;
  }

  material?.dispose();
};

const disposeObject = (object) => {
  object.traverse((child) => {
    if (!child.isMesh) {
      return;
    }

    child.geometry?.dispose();
    disposeMaterial(child.material);
  });
};

const initBusinessLaptop = (scope = document) => {
  scope.querySelectorAll('.business-sec__media').forEach((mediaEl) => {
    if (initializedMedia.has(mediaEl)) {
      return;
    }

    const canvasEl = mediaEl.querySelector('.business-sec__media-canvas');
    const screenImageEl = mediaEl.querySelector('.business-sec__screen-source img, img.business-sec__screen-source');
    const modelUrl = mediaEl.dataset.laptopModelUrl;
    const keyboardUrl = mediaEl.dataset.laptopKeyboardUrl;
    const shouldScrollScreen = mediaEl.dataset.screenScroll === 'true';

    if (!canvasEl || !screenImageEl || !modelUrl || !keyboardUrl) {
      return;
    }

    initializedMedia.add(mediaEl);

    let hasInitialized = false;
    let observer;

    const showFallback = () => {
      mediaEl.classList.remove('is-ready');
      mediaEl.classList.add('is-fallback');
    };

    const createScene = async () => {
      if (hasInitialized) {
        return;
      }

      hasInitialized = true;

      const screenUrl = screenImageEl.currentSrc || screenImageEl.src;

      if (!screenUrl) {
        showFallback();

        return;
      }

      let renderer;

      try {
        renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          canvas: canvasEl,
        });
      } catch {
        showFallback();

        return;
      }

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(32, 1, 10, 1000);
      const lightHolder = new THREE.Group();
      const macGroup = new THREE.Group();
      const lidGroup = new THREE.Group();
      const bottomGroup = new THREE.Group();
      const textureLoader = new THREE.TextureLoader();
      const gltfLoader = new GLTFLoader();
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const desktopCursorMedia = window.matchMedia(DESKTOP_CURSOR_QUERY);

      camera.position.z = 122;
      macGroup.position.copy(laptopPose.position);
      macGroup.scale.setScalar(laptopPose.scale);
      macGroup.add(lidGroup, bottomGroup);
      scene.add(macGroup, lightHolder);

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.22);
      const pointLight = new THREE.PointLight(0xfff5e1, 0.85);
      const screenLight = new THREE.RectAreaLight(0xffffff, 0, 29.4, 20);

      pointLight.position.set(0, 5, 50);
      lightHolder.add(pointLight);
      scene.add(ambientLight);

      let currentPixelRatio = Math.min(window.devicePixelRatio, FULL_PIXEL_RATIO_LIMIT);

      renderer.setPixelRatio(currentPixelRatio);
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      let isVisible = false;
      let frameId;
      let lastRenderTime = 0;
      let qualityRestoreTimer;
      let resizeFrame;
      let floatingTween;
      let introTimeline;
      let screenScrollTween;
      let environmentRenderTarget;
      let introStarted = false;
      let introFinished = false;
      let screenScrollRebased = false;
      let cursorParallaxActive = false;
      let isDisposed = false;
      let mouseMoveHandler;
      let motionMediaHandler;
      let visibilityChangeHandler;
      let updateMotionMode = () => {};
      let rebaseScreenScroll = () => {};

      const canRender = () => isVisible && !document.hidden && !isDisposed;

      const setRenderPixelRatio = (limit) => {
        const nextPixelRatio = Math.min(window.devicePixelRatio, limit);

        if (nextPixelRatio === currentPixelRatio) {
          return false;
        }

        currentPixelRatio = nextPixelRatio;
        renderer.setPixelRatio(currentPixelRatio);

        return true;
      };

      const renderFrame = (timestamp) => {
        if (!canRender()) {
          frameId = undefined;
          lastRenderTime = 0;

          return;
        }

        const elapsed = timestamp - lastRenderTime;

        if (lastRenderTime && elapsed < FRAME_INTERVAL - FRAME_TOLERANCE) {
          frameId = requestAnimationFrame(renderFrame);

          return;
        }

        frameId = undefined;
        lastRenderTime = lastRenderTime
          ? timestamp - (elapsed % FRAME_INTERVAL)
          : timestamp;
        lightHolder.quaternion.copy(camera.quaternion);
        renderer.render(scene, camera);
      };

      const requestRender = () => {
        if (frameId !== undefined || !canRender()) {
          return;
        }

        frameId = requestAnimationFrame(renderFrame);
      };

      const cancelQualityRestore = () => {
        clearTimeout(qualityRestoreTimer);
        qualityRestoreTimer = undefined;
      };

      const restoreRenderQuality = () => {
        cancelQualityRestore();

        if (setRenderPixelRatio(FULL_PIXEL_RATIO_LIMIT)) {
          requestRender();
        }
      };

      const requestAnimatedRender = () => {
        if (!canRender()) {
          return;
        }

        setRenderPixelRatio(ACTIVE_PIXEL_RATIO_LIMIT);
        cancelQualityRestore();
        qualityRestoreTimer = setTimeout(restoreRenderQuality, QUALITY_RESTORE_DELAY);
        requestRender();
      };

      const cancelRender = () => {
        if (frameId === undefined) {
          return;
        }

        cancelAnimationFrame(frameId);
        frameId = undefined;
        lastRenderTime = 0;
      };

      const updateSize = () => {
        const { width, height } = canvasEl.getBoundingClientRect();

        if (!width || !height) {
          return;
        }

        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        setRenderPixelRatio(FULL_PIXEL_RATIO_LIMIT);
        renderer.setSize(width, height, false);
        requestRender();
      };

      const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(updateSize);
      });

      const visibilityObserver = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
        isVisible = entries.some((entry) => entry.isIntersecting);

        if (!isVisible) {
          cancelRender();
          cancelQualityRestore();
          rebaseScreenScroll();
        } else {
          restoreRenderQuality();
        }

        updateMotionMode();
        requestRender();
      }, { threshold: 0 });

      visibilityChangeHandler = () => {
        if (document.hidden) {
          cancelRender();
          cancelQualityRestore();
        } else {
          restoreRenderQuality();
        }

        updateMotionMode();
        requestRender();
      };

      document.addEventListener('visibilitychange', visibilityChangeHandler);

      const cleanUp = () => {
        if (isDisposed) {
          return;
        }

        isDisposed = true;
        cancelRender();
        cancelQualityRestore();
        cancelAnimationFrame(resizeFrame);
        screenScrollTween?.scrollTrigger?.kill();
        screenScrollTween?.kill();
        introTimeline?.kill();
        floatingTween?.kill();
        gsap.killTweensOf(macGroup.rotation);
        window.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
        if (motionMediaHandler) {
          if (desktopCursorMedia.removeEventListener) {
            desktopCursorMedia.removeEventListener('change', motionMediaHandler);
          } else {
            desktopCursorMedia.removeListener(motionMediaHandler);
          }
        }
        resizeObserver?.disconnect();
        visibilityObserver?.disconnect();
        disposeObject(scene);
        environmentRenderTarget?.dispose();
        renderer.dispose();
      };

      canvasEl.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        cleanUp();
        showFallback();
      }, { once: true });

      try {
        const roomEnvironment = new RoomEnvironment();
        const pmremGenerator = new THREE.PMREMGenerator(renderer);

        try {
          environmentRenderTarget = pmremGenerator.fromScene(roomEnvironment, 0.04);
          scene.environment = environmentRenderTarget.texture;
        } finally {
          roomEnvironment.dispose();
          pmremGenerator.dispose();
        }

        const [gltf, screenTexture, keyboardTexture] = await Promise.all([
          loadGltf(gltfLoader, modelUrl),
          loadTexture(textureLoader, screenUrl),
          loadTexture(textureLoader, keyboardUrl),
        ]);

        screenTexture.colorSpace = THREE.SRGBColorSpace;
        screenTexture.flipY = false;

        let screenScrollEnd = 0;

        if (shouldScrollScreen) {
          const imageWidth = Math.max(screenTexture.image.width, 1);
          const imageHeight = Math.max(screenTexture.image.height, 1);
          const visibleTextureHeight = Math.min(
            1,
            (SCREEN_HEIGHT / SCREEN_WIDTH) * (imageWidth / imageHeight),
          );

          screenTexture.wrapS = THREE.ClampToEdgeWrapping;
          screenTexture.wrapT = THREE.ClampToEdgeWrapping;
          screenTexture.repeat.set(1, visibleTextureHeight);
          screenTexture.offset.x = 0;
          screenTexture.offset.y = 0;
          screenScrollEnd = 1 - visibleTextureHeight;
        }

        const darkPlasticMaterial = new THREE.MeshStandardMaterial({
          color: 0x000000,
          roughness: 0.9,
          metalness: 0.9,
        });
        const cameraMaterial = new THREE.MeshBasicMaterial({ color: 0x333333 });
        const baseMetalMaterial = new THREE.MeshStandardMaterial({
          color: 0x4b5058,
          roughness: 0.43,
          metalness: 0.68,
          envMapIntensity: 0.28,
        });
        const logoMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const screenMaterial = new THREE.MeshBasicMaterial({
          map: screenTexture,
          transparent: true,
          opacity: 0,
          side: THREE.BackSide,
        });
        const keyboardMaterial = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          alphaMap: keyboardTexture,
          transparent: true,
          depthWrite: false,
        });
        const lid = gltf.scene.getObjectByName('_top');
        const bottom = gltf.scene.getObjectByName('_bottom');

        if (!lid || !bottom) {
          throw new Error('Laptop model structure is invalid.');
        }

        lidGroup.add(lid);
        bottomGroup.add(bottom);

        lid.traverse((child) => {
          if (!child.isMesh) {
            return;
          }

          if (child.name === 'lid') {
            child.material = baseMetalMaterial;
          } else if (child.name === 'logo') {
            child.material = logoMaterial;
          } else if (child.name === 'screen-frame') {
            child.material = darkPlasticMaterial;
          } else if (child.name === 'camera') {
            child.material = cameraMaterial;
          }
        });

        bottom.traverse((child) => {
          if (!child.isMesh) {
            return;
          }

          if (child.name === 'base') {
            child.material = baseMetalMaterial;
          } else if (['legs', 'keyboard', 'inner'].includes(child.name)) {
            child.material = darkPlasticMaterial;
          }
        });

        const screenMesh = new THREE.Mesh(
          new THREE.PlaneGeometry(SCREEN_WIDTH, SCREEN_HEIGHT),
          screenMaterial,
        );
        const darkScreen = new THREE.Mesh(
          new THREE.PlaneGeometry(SCREEN_WIDTH, SCREEN_HEIGHT),
          darkPlasticMaterial,
        );
        const keyboardKeys = new THREE.Mesh(
          new THREE.PlaneGeometry(27.7, 11.6),
          keyboardMaterial,
        );

        screenMesh.position.set(0, 10.5, -0.11);
        screenMesh.rotation.set(Math.PI, 0, 0);
        darkScreen.position.set(0, 10.5, -0.111);
        darkScreen.rotation.set(Math.PI, Math.PI, 0);
        screenLight.position.set(0, 10.5, 0);
        screenLight.rotation.set(Math.PI, 0, 0);
        keyboardKeys.rotation.set(-0.5 * Math.PI, 0, 0);
        keyboardKeys.position.set(0, 0.045, 7.21);

        lidGroup.add(screenMesh, darkScreen, screenLight);
        bottomGroup.add(keyboardKeys);

        updateSize();
        resizeObserver?.observe(mediaEl);
        visibilityObserver?.observe(mediaEl);
        mediaEl.classList.remove('is-fallback');
        mediaEl.classList.add('is-ready');

        if (!visibilityObserver) {
          isVisible = true;
          requestRender();
        }

        const createScreenScroll = () => {
          if (!shouldScrollScreen || screenScrollTween) {
            return;
          }

          const screenScrollTrigger = mediaEl.closest('.business-sec') || mediaEl;
          const screenScrollTriggerTop = screenScrollTrigger.getBoundingClientRect().top
            + window.scrollY;
          const screenScrollStartOffset = window.scrollY - screenScrollTriggerTop;

          screenTexture.offset.y = 0;
          screenScrollTween = gsap.to(screenTexture.offset, {
            y: screenScrollEnd,
            ease: 'none',
            onUpdate: requestAnimatedRender,
            scrollTrigger: {
              trigger: screenScrollTrigger,
              start: () => screenScrollTrigger.getBoundingClientRect().top
                + window.scrollY
                + screenScrollStartOffset,
              end: 'bottom 5%',
              scrub: 0.7,
              invalidateOnRefresh: true,
              onRefresh: requestRender,
            },
          });
        };

        rebaseScreenScroll = () => {
          if (!shouldScrollScreen || !screenScrollTween) {
            return;
          }

          if (!introFinished || screenScrollRebased || isVisible) {
            return;
          }

          screenScrollRebased = true;
          screenScrollTween.scrollTrigger?.kill();
          screenScrollTween.kill();
          screenScrollTween = gsap.to(screenTexture.offset, {
            y: screenScrollEnd,
            ease: 'none',
            onUpdate: requestAnimatedRender,
            scrollTrigger: {
              trigger: mediaEl,
              start: 'top top',
              end: 'bottom top',
              scrub: 0.7,
              invalidateOnRefresh: true,
              onRefresh: requestRender,
            },
          });
          ScrollTrigger.refresh();
        };

        if (reducedMotion) {
          macGroup.position.copy(laptopPose.position);
          macGroup.rotation.copy(laptopPose.rotation);
          lidGroup.rotation.x = laptopPose.lidRotation;
          screenMaterial.opacity = 0.96;
          screenLight.intensity = 1.5;
          introFinished = true;
          createScreenScroll();
          ScrollTrigger.refresh();
          requestRender();

          return;
        }

        const completeIntro = () => {
          introFinished = true;
          createScreenScroll();
          updateMotionMode();
          ScrollTrigger.refresh();
          requestAnimatedRender();
        };

        introTimeline = gsap.timeline({
          paused: true,
          onUpdate: requestAnimatedRender,
          onComplete: completeIntro,
        });
        macGroup.rotation.copy(laptopPose.rotation);
        introTimeline
          .addLabel('introDelay', 0)
          .addLabel('entry', 0.2)
          .addLabel('lidOpen', 0.55)
          .addLabel('screenOn', 1.6)
          .addLabel('lidSettle', 2.05)
          .addLabel('introComplete', 2.35)
          .fromTo(macGroup.position, {
            x: 55,
            y: laptopPose.position.y - 2,
            z: laptopPose.position.z - 4,
          }, {
            x: laptopPose.position.x,
            y: laptopPose.position.y,
            z: laptopPose.position.z,
            duration: 1.4,
            ease: 'power2.out',
          }, 'entry')
          .fromTo(macGroup.rotation, {
            x: 0.015 * Math.PI,
            y: 0.16 * Math.PI,
            z: laptopPose.rotation.z,
          }, {
            x: laptopPose.rotation.x,
            y: laptopPose.rotation.y,
            z: laptopPose.rotation.z,
            duration: 1.5,
            ease: 'sine.out',
          }, 'entry')
          .fromTo(lidGroup.rotation, {
            x: 0.5 * Math.PI,
          }, {
            x: laptopPose.lidRotation - 0.018 * Math.PI,
            duration: 1.55,
            ease: 'sine.inOut',
          }, 'lidOpen')
          .to(lidGroup.rotation, {
            x: laptopPose.lidRotation,
            duration: 0.22,
            ease: 'sine.inOut',
          }, 'lidSettle')
          .to(screenMaterial, {
            opacity: 0.96,
            duration: 0.65,
            ease: 'sine.out',
          }, 'screenOn')
          .to(screenLight, {
            intensity: 1.5,
            duration: 0.65,
            ease: 'sine.out',
          }, 'screenOn');

        floatingTween = gsap.to([lidGroup.position, bottomGroup.position], {
          y: '+=0.5',
          duration: 1.5,
          ease: 'power1.inOut',
          repeat: -1,
          yoyo: true,
          paused: true,
          onUpdate: requestAnimatedRender,
        });
        const setRotationX = gsap.quickTo(macGroup.rotation, 'x', {
          duration: 1,
          ease: 'power3.out',
          onUpdate: requestAnimatedRender,
        });
        const setRotationY = gsap.quickTo(macGroup.rotation, 'y', {
          duration: 1,
          ease: 'power3.out',
          onUpdate: requestAnimatedRender,
        });

        const resetLaptopRotation = () => {
          setRotationX(laptopPose.rotation.x);
          setRotationY(laptopPose.rotation.y);
        };

        const disableCursorParallax = () => {
          if (!cursorParallaxActive) {
            return;
          }

          window.removeEventListener('mousemove', mouseMoveHandler);
          cursorParallaxActive = false;
          resetLaptopRotation();
        };

        mouseMoveHandler = (event) => {
          if (!isVisible || document.hidden || !introFinished || !desktopCursorMedia.matches) {
            return;
          }

          const pointerX = event.clientX / Math.max(window.innerWidth, 1) * 2 - 1;
          const pointerY = event.clientY / Math.max(window.innerHeight, 1) * 2 - 1;

          setRotationX(laptopPose.rotation.x - pointerY * 0.012 * Math.PI);
          setRotationY(laptopPose.rotation.y + pointerX * 0.025 * Math.PI);
        };

        updateMotionMode = () => {
          const canAnimate = isVisible && !document.hidden;

          if (!introFinished) {
            disableCursorParallax();
            floatingTween.pause();

            if (!canAnimate) {
              introTimeline.pause();

              return;
            }

            if (!introStarted) {
              introStarted = true;
              introTimeline.play(0);

              return;
            }

            introTimeline.resume();

            return;
          }

          if (reducedMotion) {
            return;
          }

          const useCursorParallax = canAnimate && desktopCursorMedia.matches;

          if (useCursorParallax) {
            floatingTween.pause(0);

            if (!cursorParallaxActive) {
              window.addEventListener('mousemove', mouseMoveHandler, { passive: true });
              cursorParallaxActive = true;
            }

            return;
          }

          disableCursorParallax();

          if (canAnimate && !desktopCursorMedia.matches) {
            floatingTween.play(0);

            return;
          }

          floatingTween.pause(0);
        };

        motionMediaHandler = updateMotionMode;
        if (desktopCursorMedia.addEventListener) {
          desktopCursorMedia.addEventListener('change', motionMediaHandler);
        } else {
          desktopCursorMedia.addListener(motionMediaHandler);
        }

        updateMotionMode();
        ScrollTrigger.refresh();
      } catch {
        cleanUp();
        showFallback();
      }
    };

    if (typeof IntersectionObserver === 'undefined') {
      createScene();

      return;
    }

    observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      observer.disconnect();
      createScene();
    }, { rootMargin: '200px 0px' });

    observer.observe(mediaEl);
  });
};

initBusinessLaptop();
